import { EventEmitter, Injectable } from '@angular/core';
import { HttpClient, HttpParams, HttpHeaders } from '@angular/common/http';
import { BehaviorSubject, Observable, ReplaySubject, Subscriber } from 'rxjs';
import { map } from 'rxjs/operators';

// utility
import { loggedInUser } from '../helpers/utils';

import { getAllUsers } from './../helpers/utils';

// types
import { PublicUserTO, TfaPending, User } from '../models/auth.models';
import { Environment } from 'src/libs/env/environment';
import { UserSocioReportModel } from 'src/app/libs/services/reports/model/user-socio-report.model';
import { Page } from 'src/app/shared/pagination';

let users: User[] = getAllUsers();

export interface RefreshEvent {
  next: (credential: User) => void;
  error: (error: any) => void;
  credential?: User | null;
}

@Injectable({ providedIn: 'root' })
export class AuthenticationService {
  private intervalId: any;

  private sessionTimeoutId: any;

  private refreshQueue: Subscriber<User>[];

  public readonly onInit: ReplaySubject<void>;

  public readonly onRefresh: EventEmitter<RefreshEvent>;

  public readonly onForbidden: EventEmitter<void>;

  public readonly onInvalidate: EventEmitter<void>;

  public readonly onUnauthorized: EventEmitter<void>;

  public readonly onSessionExpired: EventEmitter<void>;

  private rolesBehaviorSubject = new BehaviorSubject<string[]>([]);

  user: User | null = null;

  constructor(private http: HttpClient, private environment: Environment) {
    this.refreshQueue = [];
    this.onRefresh = new EventEmitter<RefreshEvent>();

    this.onInit = new ReplaySubject<void>(1);
    this.onForbidden = new EventEmitter<void>();
    this.onInvalidate = new EventEmitter<void>();
    this.onUnauthorized = new EventEmitter<void>();
    this.onSessionExpired = new EventEmitter<void>();
  }

  public init(): void {
    localStorage.removeItem('tfaVerifiedLogins');

    let data = localStorage.getItem('currentUser');

    if (data) {
      try {
        this.user = JSON.parse(data);
      } catch {
        this.user = null;
      }
    }

    if (this.user && this.isSessionExpired(this.user)) {
      this.logout();
      this.onInit.next();
      return;
    }

    if (this.user) {
      this.scheduleSessionExpiration(this.user.sessionExpiresAt);
      this.emitRefresh().subscribe(() => this.onInit.next());
    } else {
      this.onInit.next();
    }
  }

  /**
   * Returns the current user
   */
  public currentUser(): User | null {
    if (!this.user) {
      this.user = loggedInUser();
    }
    if (this.user && this.isSessionExpired(this.user)) {
      this.logout();
      return null;
    }
    return this.user;
  }

  /**
   * Performs the login auth
   * @param login login of user
   * @param password password of user
   */
  login(login: string, password: string): Observable<User> {
    return this.http
      .post<User>(`${this.environment.api.apiURL}/api/v1/auth/auth/login?limitSession=true`, {
        login,
        password,
        grantType: 'password',
        modulesId: [
          '12472e7c-4abc-11e9-a4d1-0242ac110003', //Auth
          '07498bb5-d7b4-4a4d-82f8-5eb9e2497478', //Socio
          'e380f282-0015-40a9-aa06-e20b1f291d53', //Adm
        ],
      })
      .pipe(
        map((credential) => {
          if (credential && credential.accessToken) {
            this.setCredential(credential);
          }
          return credential;
        })
      );
  }

  /**
   * Performs the login auth verifying the TFA code
   * @param tfaToken token returned by the login step
   * @param code code of TFA
   */
  verifyCode(tfaToken: string, code: string): Observable<User> {
    return this.http
      .post<User>(`${this.environment.api.apiURL}/api/v1/auth/auth/verify-code`, {
        tfaToken,
        code,
        modulesId: [
          '12472e7c-4abc-11e9-a4d1-0242ac110003', //Auth
          '07498bb5-d7b4-4a4d-82f8-5eb9e2497478', //Socio
          'e380f282-0015-40a9-aa06-e20b1f291d53', //Adm
        ],
      })
      .pipe(
        map((credential) => {
          if (credential && credential.accessToken) {
            this.setCredential(credential);
          }
          return credential;
        })
      );
  }

  automaticLogin(userId: string): Observable<User> {
    return this.http.get<User>(`${this.environment.api.apiURL}/public/api/v1/auth/auth/login/${userId}`,
    )
      .pipe(
        map((credential) => {
          if (credential && credential.accessToken) {
            this.setCredential(credential);
          }
          return credential;
        })
      );
  }


  changePassword(passwordTO: any, token: string): Observable<any> {
    let params: HttpParams = new HttpParams().append('requestToken', token);
    return this.http.put(`${this.environment.api.apiURL}/api/v1/auth/auth/user-request/password`, passwordTO, { params });

  }

  findUserByCpf(cpf: string): Observable<any> {
    return this.http.get(`${this.environment.api.apiURL}/api/v1/auth/users/user-by-cpf/${cpf}`);
  }

  listCheckBoxStatuses(): Observable<any> {
    return this.http.get(`${this.environment.api.apiURL}/api/v1/auth/users-statuses`);
  }

  changeUserStatus(cpf: string, newStatus: string): Observable<any> {
    return this.http.put(`${this.environment.api.apiURL}/api/v1/auth/users/changeStatusByCpf/${cpf}/${newStatus}`, {
    });
  }

  deleteUser(id: string): Observable<any> {
    return this.http.put(`${this.environment.api.apiURL}/api/v1/auth/users/delete/${id}`, {
    });
  }

  changePasswordForUser(userId: string, passwordTO: any): Observable<any> {
    return this.http.put(`${this.environment.api.apiURL}/api/v1/auth/users/password/${userId}`, passwordTO);
  }

  public getAll(filter?: any): Observable<Page<UserSocioReportModel>> {
    let params = new HttpParams();
    let pageSize = 10;
    let pageNumber = 0;
    let sortBy = 'id';
    let sortDirection = 'asc';

    if (filter) {
      if (filter.pageNumber !== undefined) {
        pageNumber = filter.pageNumber;
      }

      if (filter.pageSize !== undefined) {
        pageSize = filter.pageSize;
      }

      if (filter.sortBy) {
        sortBy = filter.sortBy;
      }

      if (filter.sortDirection) {
        sortDirection = filter.sortDirection;
      }

      if (filter.name) {
        params = params.append('name', filter.name);
      }

      if (filter.login) {
        params = params.append('login', filter.login);
      }

      if (filter.mail) {
        params = params.append('mail', filter.mail);
      }

      if (filter.statusId) {
        params = params.append('statusId', filter.statusId);
      }

      if (filter.isDeleted !== undefined) {
        params = params.append('isDeleted', filter.isDeleted.toString());
      }

      if (filter.socio !== undefined) {
        params = params.append('socio', filter.socio.toString());
      }
    }

    params = params.append('pageNumber', pageNumber.toString());
    params = params.append('pageSize', pageSize.toString());
    params = params.append('sortBy', sortBy);
    params = params.append('sortDirection', sortDirection);

    return this.http.get<Page<UserSocioReportModel>>(
      `${this.environment.api.apiURL}/api/v1/auth/users/getAllUsers`,
      { params }
    );
  }


  public getUserStatuses(): Observable<any> {
    return this.http.get(`${this.environment.api.apiURL}/api/v1/auth/users-statuses`);
  }

  refresh(refreshToken: string): Observable<User> {
    return this.http
      .post<User>(`${this.environment.api.apiURL}/api/v1/auth/auth/refresh`, {
        modulesId: [
          '12472e7c-4abc-11e9-a4d1-0242ac110003', //Auth
          '07498bb5-d7b4-4a4d-82f8-5eb9e2497478', //Socio
          'e380f282-0015-40a9-aa06-e20b1f291d53', //Adm
        ],
        refreshToken: refreshToken,
      })
      .pipe(
        map((credential) => {
          this.setRoles(credential.roles);
          return credential;
        })
      );
  }

  recoverPassword(email: string) {
    const params = new HttpParams().append('email', email);
    return this.http.get(
      `${this.environment.api.apiURL}/api/v1/auth/auth/user-request/recover-password-club`,
      { params }
    );
  }

  recoverPasswordBackoffice(email: string) {
    const params = new HttpParams().append('email', email);
    return this.http.get(
      `${this.environment.api.apiURL}/api/v1/auth/auth/user-request/recover-password`,
      { params }
    );
  }

  public emitRefresh(): Observable<User> {
    return new Observable((subscriber: Subscriber<User>) => {
      if (this.refreshQueue.length === 0) {
        this.onRefresh.emit({
          credential: this.user,
          next: (credential: User) => {
            this.setCredential(credential, false);
            this.refreshQueue.forEach((data: Subscriber<User>) =>
              data.next(credential)
            );
            this.refreshQueue = [];
          },
          error: (error: any) => {
            this.emitUnauthorized();
            this.refreshQueue.forEach((data: Subscriber<User>) =>
              data.error(error)
            );
            this.refreshQueue = [];
          },
        });
      }

      this.refreshQueue.push(subscriber);
    });
  }

  public setCredential(credential: User, emitEvent: boolean = true) {
    if (credential && credential.accessToken) {
      if (!emitEvent && !credential.sessionExpiresAt && this.user?.sessionExpiresAt) {
        credential.sessionExpiresAt = this.user.sessionExpiresAt;
      }
      this.user = credential;
      localStorage.setItem('currentUser', JSON.stringify(credential));
      this.setRoles(credential.roles);
      if (credential.accessExpiresIn) {
        const expiresIn = Math.max((credential.accessExpiresIn - 60) * 1000, 5000);

        if (this.intervalId) {
          clearInterval(this.intervalId);
        }

        this.intervalId = setInterval(() => {
          clearInterval(this.intervalId);
          this.emitRefresh().subscribe(() => this.onInit.next());
        }, expiresIn);
      }

      this.scheduleSessionExpiration(credential.sessionExpiresAt);

      if (emitEvent) {
        this.onInit.next();
      }
    }
  }

  public getLoggedPublicUserById(): Observable<PublicUserTO> {
    return this.http.get<PublicUserTO>(`${this.environment.api.apiURL}/public/api/v1/auth/users/${this.user.userId}`);
  }

  public getPublicUserById(id: string): Observable<PublicUserTO> {
    return this.http.get<PublicUserTO>(`${this.environment.api.apiURL}/public/api/v1/auth/users/${id}`);
  }

  public getListPublicUser(ids: string[]): Observable<PublicUserTO[]> {
    return this.http.post<PublicUserTO[]>(`${this.environment.api.apiURL}/public/api/v1/auth/users/findByIds`, ids);
  }

  changePasswordLoggedIn(newPassword: string, passwordConfirmation: string, authToken: string): Observable<void> {
    const headers = new HttpHeaders({
      'Authorization': `Bearer ${authToken}`,
      'Content-Type': 'application/json'
    });

    const body = {
      newPassword,
      passwordConfirmation
    };

    return this.http.put<void>(`${this.environment.api.apiURL}/api/v1/auth/users/password`, body, { headers });
  }

  /**
   * Performs the signup auth
   * @param name name of user
   * @param email email of user
   * @param password password of user
   */
  signup(name: string, email: string, password: string): Observable<User> {
    return this.http
      .post<User>(`${this.environment.api.apiURL}/api/signup`, {
        name,
        email,
        password,
      })
      .pipe(map((user) => user));
  }

  /**
   * Logout the user
   */
  logout(): void {
    // remove user from session storage to log user out
    localStorage.removeItem("contextTenant");
    localStorage.removeItem('currentUser');
    this.user = null;
    this.clearTimers();
  }

  private clearTimers(): void {
    if (this.intervalId) {
      clearInterval(this.intervalId);
      this.intervalId = null;
    }
    if (this.sessionTimeoutId) {
      clearTimeout(this.sessionTimeoutId);
      this.sessionTimeoutId = null;
    }
  }

  private scheduleSessionExpiration(sessionExpiresAt?: number): void {
    if (this.sessionTimeoutId) {
      clearTimeout(this.sessionTimeoutId);
      this.sessionTimeoutId = null;
    }
    if (!sessionExpiresAt) {
      return;
    }
    // setTimeout estoura acima de 2^31-1 ms; limitamos para nunca disparar antes da hora
    const delay = Math.min(Math.max(sessionExpiresAt - Date.now(), 0), 2147483647);
    this.sessionTimeoutId = setTimeout(() => {
      if (Date.now() >= sessionExpiresAt) {
        this.expireSession();
      } else {
        this.scheduleSessionExpiration(sessionExpiresAt);
      }
    }, delay);
  }

  private expireSession(): void {
    // Outra aba pode ter feito login e gravado uma credencial mais nova: adota-a em vez de deslogar
    const stored = this.readStoredUser();
    if (stored && !this.isSessionExpired(stored) && stored.sessionExpiresAt !== this.user?.sessionExpiresAt) {
      this.user = stored;
      this.setRoles(stored.roles);
      this.scheduleSessionExpiration(stored.sessionExpiresAt);
      return;
    }
    this.clearTimers();
    this.onSessionExpired.emit();
    this.invalidate();
  }

  private readStoredUser(): User | null {
    try {
      return JSON.parse(localStorage.getItem('currentUser'));
    } catch {
      return null;
    }
  }

  public isSessionExpired(user: User | null): boolean {
    return !user?.sessionExpiresAt || Date.now() >= user.sessionExpiresAt;
  }

  public setTfaPending(pending: TfaPending): void {
    sessionStorage.setItem('tfaPending', JSON.stringify(pending));
  }

  public getTfaPending(): TfaPending | null {
    try {
      const pending = JSON.parse(sessionStorage.getItem('tfaPending'));
      return pending && typeof pending === 'object' ? pending : null;
    } catch {
      return null;
    }
  }

  public clearTfaPending(): void {
    sessionStorage.removeItem('tfaPending');
  }

  public consumeTfaQrCode(): void {
    const pending = this.getTfaPending();
    if (pending?.secretImageUri) {
      delete pending.secretImageUri;
      this.setTfaPending(pending);
    }
  }

  public invalidate(): void {
    this.logout();
    this.onInvalidate.emit();
  }

  public emitUnauthorized(): void {
    this.onUnauthorized.emit();
  }

  public emitForbidden(): void {
    this.onForbidden.emit();
  }

  public get isIni(): boolean {
    return !!this.intervalId;
  }

  public get credential(): Partial<User> {
    return this.user || {};
  }

  setRoles(roles: string[]): void {
    this.rolesBehaviorSubject.next(roles)
  }


  getRolesObservable(): Observable<string[]> {
    return this.rolesBehaviorSubject.asObservable();
  }
}
